import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtMinutes, fmtDateShort, currentMonth } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

const STATUS = {
  auto: { label: "Automatisch", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  approved: { label: "Freigegeben", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  pending: { label: "Wartet auf Freigabe", cls: "bg-purple-50 text-purple-700 border-purple-200" },
};

export default function MeineZeiten() {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), start: "08:00", end: "16:00", note: "" });

  const load = async (m) => {
    const r = await api.get("/sessions", { params: { month: m } });
    setRows(r.data || []);
  };

  useEffect(() => {
    load(month);
  }, [month]);

  const submit = async () => {
    try {
      await api.post("/sessions/manual", form);
      toast.success("Nachtrag eingereicht – wartet auf Freigabe");
      setOpen(false);
      load(month);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const total = rows.reduce((s, r) => s + (r.minutes || 0), 0);

  return (
    <div>
      <PageHeader
        title="Meine Zeiten"
        subtitle="Übersicht deiner erfassten Arbeitszeiten."
        testid="zeiten-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="manual-entry-open" className="brand-bg text-white hover:opacity-90">
                <Plus className="h-4 w-4 mr-2" /> Nachtrag
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="manual-entry-dialog">
              <DialogHeader>
                <DialogTitle>Zeit nachtragen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input type="date" data-testid="manual-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Von</Label>
                    <Input type="time" data-testid="manual-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Bis</Label>
                    <Input type="time" data-testid="manual-end" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Begründung</Label>
                  <Input data-testid="manual-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="z. B. Vergessen einzustempeln" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="manual-submit" className="brand-bg text-white hover:opacity-90">Einreichen</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-3 mb-4">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="month-select" />
        <Card className="px-4 py-2 border-slate-200 flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-400">Gesamt</span>
          <span className="font-mono font-bold text-slate-900" data-testid="month-total">{fmtMinutes(total)}</span>
        </Card>
      </div>

      <Card className="border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0 ? (
          <EmptyState icon={ListChecks} title="Keine Einträge" subtitle="Für diesen Monat wurden noch keine Zeiten erfasst." testid="zeiten-empty" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Von</TableHead>
                <TableHead>Bis</TableHead>
                <TableHead>Dauer</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`session-row-${r.id}`}>
                  <TableCell className="font-medium">{fmtDateShort(r.date)}</TableCell>
                  <TableCell>{r.start}</TableCell>
                  <TableCell>{r.end || "läuft…"}</TableCell>
                  <TableCell className="font-mono">{fmtMinutes(r.minutes)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
